import React, { useState, useEffect, useMemo } from 'react';
import Select, { SingleValue } from 'react-select';
import toast from 'react-hot-toast';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './Rsvp.css';

interface Guest {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  attending_welcome_party: boolean | null;
  attending_wedding: boolean | null;
  attending_pool_party: boolean | null;
  staying_at_venue: boolean | null;
}

interface Ticket {
  id: number;
  name: string;
  rsvpd: boolean;
  guests: Guest[];
  dietary_restrictions: string | null;
  comments: string | null;
}

const RSVP_DISABLED = true;

const Rsvp: React.FC = () => {
  const [activeTab, setActiveTab] = useState('myRsvp');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [email, setEmail] = useState('');
  const [dietaryRestrictions, setDietaryRestrictions] = useState('');
  const [comments, setComments] = useState('');

  const fetchTickets = () => {
    fetch('/api/tickets')
      .then(response => response.json())
      .then(data => setTickets(data))
      .catch(error => console.error('Error fetching tickets:', error));
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleTicketChange = (selectedOption: SingleValue<{ value: string; label: string; }>) => {
    if (selectedOption) {
      const ticket = tickets.find(t => t.id === parseInt(selectedOption.value));
      setSelectedTicket(ticket || null);
      // Set dietary restrictions and comments from the ticket
      setDietaryRestrictions(ticket?.dietary_restrictions || '');
      setComments(ticket?.comments || '');
      // Set email from first guest (all guests share the same email)
      setEmail(ticket?.guests[0]?.email || '');
    } else {
      setSelectedTicket(null);
      setDietaryRestrictions('');
      setComments('');
      setEmail('');
    }
  };

  const handleGuestChange = (guestId: number, field: keyof Guest, value: boolean | null) => {
    if (RSVP_DISABLED) return;
    if (selectedTicket) {
      const updatedGuests = selectedTicket.guests.map(guest => {
        if (guest.id === guestId) {
          const newValue = guest[field] === value ? null : value;
          return { ...guest, [field]: newValue };
        }
        return guest;
      });
      setSelectedTicket({ ...selectedTicket, guests: updatedGuests });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (RSVP_DISABLED) return;
    if (selectedTicket) {
      // Check if all guests are not attending any events
      const allGuestsNotAttending = selectedTicket.guests.every(guest =>
        guest.attending_welcome_party === false &&
        guest.attending_wedding === false &&
        guest.attending_pool_party === false
      );

      // Validate that all attendance fields are selected
      for (const guest of selectedTicket.guests) {
        if (guest.attending_welcome_party === null || guest.attending_wedding === null || guest.attending_pool_party === null) {
          alert('Please make a selection for all attendance options for all guests.');
          return;
        }

        // Only require staying_at_venue if at least one guest is attending an event
        if (!allGuestsNotAttending && guest.staying_at_venue === null) {
          alert('Please make a selection for all attendance options for all guests.');
          return;
        }
      }

      // Create a new guests array with the updated email for all guests
      const guestsWithUpdatedEmail = selectedTicket.guests.map(guest => ({
        ...guest,
        email: email,
      }));

      const payload = {
        ticketId: selectedTicket.id,
        guests: guestsWithUpdatedEmail,
        dietary_restrictions: dietaryRestrictions,
        comments: comments,
      };

      try {
        const response = await fetch('/api/guests', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          toast.success('RSVP submitted successfully!');
          fetchTickets(); // Re-fetch tickets after successful submission
          setTimeout(() => {
            setActiveTab('guestList');
            window.scrollTo(0, 0);
          }, 1500); // Wait 1.5 seconds before switching tabs
        } else {
          const errorData = await response.json();
          toast.error(`Failed to submit RSVP: ${errorData.error}`);
        }
      } catch (error) {
        console.error('Error submitting RSVP:', error);
        alert('An error occurred while submitting your RSVP.');
      }
    }
  };

  const renderEventCheckboxes = (eventName: 'attending_welcome_party' | 'attending_wedding' | 'attending_pool_party', eventTitle: string) => {
    return (
      <div className="event-section mb-4">
        <h5 className="rsvp-text">{eventTitle}</h5>
        {selectedTicket && selectedTicket.guests.map(guest => (
          <div key={guest.id} className="row mb-2 align-items-center event-row">
            <div className="col-4 rsvp-text">{guest.first_name}</div>
            <div className="col-8">
              <div className="rsvp-button-group">
                <button
                  type="button"
                  className={`rsvp-button ${guest[eventName] === true ? 'active' : ''}`}
                  onClick={() => handleGuestChange(guest.id, eventName, true)}
                  disabled={RSVP_DISABLED}
                >
                  Attending
                </button>
                <button
                  type="button"
                  className={`rsvp-button ${guest[eventName] === false ? 'active' : ''}`}
                  onClick={() => handleGuestChange(guest.id, eventName, false)}
                  disabled={RSVP_DISABLED}
                >
                  Can't Make It
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Group tickets by confirmed status and sort alphabetically
  const confirmedTickets = useMemo(() => {
    return tickets
      .filter(ticket =>
        ticket.guests.some(guest =>
          guest.attending_welcome_party === true ||
          guest.attending_wedding === true ||
          guest.attending_pool_party === true
        )
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tickets]);

  const invitedTickets = useMemo(() => {
    return tickets
      .filter(ticket =>
        ticket.guests.every(guest =>
          (guest.attending_welcome_party === null || guest.attending_wedding === null || guest.attending_pool_party === null) &&
          !(guest.attending_welcome_party === true || guest.attending_wedding === true || guest.attending_pool_party === true)
        )
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tickets]);

  return (
    <section className="rsvp-section py-5">
      <div className="container">
        <div className="text-center rsvp-closed-banner mb-4" role="alert">
          RSVPs are currently closed. If you'd like to change your RSVP, please contact us directly at{' '}
          <a href="mailto:flotofamily2025@gmail.com">flotofamily2025@gmail.com</a>.
        </div>
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'myRsvp' ? 'active' : ''}`}
              onClick={() => setActiveTab('myRsvp')}
            >
              My RSVP
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'guestList' ? 'active' : ''}`}
              onClick={() => setActiveTab('guestList')}
            >
              Guest List
            </button>
          </li>
        </ul>

        <div className="tab-content">
          {activeTab === 'myRsvp' && (
            <div className="tab-pane fade show active">
              <div className="row">
                <div className="col-md-8 mx-auto">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label rsvp-text">Your Name</label>
                      <Select
                        classNamePrefix="rsvp-name-select"
                        options={[...tickets].sort((a, b) => a.name.localeCompare(b.name)).map(ticket => ({ value: ticket.id.toString(), label: ticket.name }))}
                        onChange={handleTicketChange}
                        placeholder="Select your ticket or start typing..."
                        isDisabled={RSVP_DISABLED}
                      />
                    </div>

                    {selectedTicket && (
                      <div>
                        <div className="mb-3">
                          <label htmlFor="email" className="form-label rsvp-text">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            placeholder="Your email..."
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={RSVP_DISABLED}
                          />
                        </div>

                        {renderEventCheckboxes('attending_welcome_party', 'October 1st: Welcome Party')}
                        {renderEventCheckboxes('attending_wedding', 'October 2nd: Wedding')}
                        {renderEventCheckboxes('attending_pool_party', 'October 3rd: Pool Party')}

                        <div className="event-section mb-4">
                          <h5 className="rsvp-text">
                            Are You Interested in Staying at the Venue?
                                                        <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id="tooltip-top" className="rsvp-tooltip">Room rates: $280-$380 per night (for 2 guests), breakfast included. Some rooms accommodate up to 4 guests for an additional charge.</Tooltip>}
                            >
                              <i className="bi bi-info-circle info-icon"></i>
                            </OverlayTrigger>
                          </h5>
                          {selectedTicket.guests.map(guest => (
                            <div key={guest.id} className="row mb-2 align-items-center event-row">
                              <div className="col-4 rsvp-text">{guest.first_name}</div>
                              <div className="col-8">
                                <div className="rsvp-button-group">
                                  <button
                                    type="button"
                                    className={`rsvp-button ${guest.staying_at_venue === true ? 'active' : ''}`}
                                    onClick={() => handleGuestChange(guest.id, 'staying_at_venue', true)}
                                    disabled={RSVP_DISABLED}
                                  >
                                    I'd love to!
                                  </button>
                                  <button
                                    type="button"
                                    className={`rsvp-button ${guest.staying_at_venue === false ? 'active' : ''}`}
                                    onClick={() => handleGuestChange(guest.id, 'staying_at_venue', false)}
                                    disabled={RSVP_DISABLED}
                                  >
                                    I'll stay elsewhere
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="event-section mb-4">
                          <h5 className="rsvp-text">Dietary Restrictions?</h5>
                          <div className="mb-3">
                            <textarea
                              className="form-control"
                              id="dietaryRestrictions"
                              placeholder="Please list any dietary restrictions (and specify for whom)..."
                              value={dietaryRestrictions}
                              onChange={(e) => setDietaryRestrictions(e.target.value)}
                              rows={3}
                              disabled={RSVP_DISABLED}
                            ></textarea>
                          </div>
                        </div>

                        <div className="event-section mb-4">
                          <h5 className="rsvp-text">Additional Comments or Questions?</h5>
                          <div className="mb-3">
                            <textarea
                              className="form-control"
                              id="comments"
                              placeholder="Comments, questions, note to couple, marriage advice, song requests, etc"
                              value={comments}
                              onChange={(e) => setComments(e.target.value)}
                              rows={3}
                              disabled={RSVP_DISABLED}
                            ></textarea>
                          </div>
                        </div>

                        <button type="submit" className="btn-like-tab" disabled={RSVP_DISABLED}>Submit</button>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'guestList' && (
            <div className="tab-pane fade show active">
              <h4 className="rsvp-text">Confirmed Guests</h4>
              <ul className="rsvp-list">
                {confirmedTickets.flatMap(ticket =>
                  ticket.guests
                    .filter(guest =>
                      guest.attending_welcome_party === true ||
                      guest.attending_wedding === true ||
                      guest.attending_pool_party === true
                    )
                    .map(guest => (
                      <li key={guest.id}>
                        {guest.first_name} {guest.last_name}
                      </li>
                    ))
                )}
              </ul>

              <h4 className="mt-5 rsvp-text">Invited Guests (Not Yet RSVP'd)</h4>
              <ul className="rsvp-list">
                {invitedTickets.flatMap(ticket =>
                  ticket.guests.map(guest => (
                    <li key={guest.id}>
                      {guest.first_name} {guest.last_name}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Rsvp;